import { useCallback } from 'react';
import { useAxiosInstance } from '@gov-nx/api/axios';
import {
	useUnauthorizedResponse,
	isResponseStatus,
} from '@gov-nx/api/errors/utils';
import type { AutorizaceSluzbaAutorizaceDto } from '@gov-nx/api/generated/autorizace.types';
import { usePoQuery } from '@gov-nx/api/hooks/usePoQuery';
import { vypisSeznamSluzebProAutorizaci } from '@gov-nx/api/queries/authQuery';
import { useAppContext } from '@gov-nx/core/app/context/AppContext';
import { useExtraConfigContext } from '@gov-nx/core/app/extra-config/ExtraConfigProvider';
import { useAutorizaceStore } from '@gov-nx/store/autorizace/store';

const queryKey = ['seznam-sluzeb-app-portal-obcana'];

export const useServiceListLoader = () => {
	const { axiosInstance } = useAxiosInstance();
	const { isUnauthorizedResponse } = useUnauthorizedResponse();
	const { setAppError } = useAppContext();
	const {
		extraConfig: {
			authorization: { enabled: isAuthorizationEnabled },
		},
	} = useExtraConfigContext();
	const setServicesList = useAutorizaceStore('setServicesList');

	const initQuery = usePoQuery(
		{
			queryFn: async (): Promise<AutorizaceSluzbaAutorizaceDto[]> => {
				if (!isAuthorizationEnabled) {
					setServicesList([]);
					return [];
				}

				const data = await vypisSeznamSluzebProAutorizaci(axiosInstance);

				const list = data.data.seznam ?? [];
				setServicesList(list);

				return list;
			},
			onError: (error) => {
				if (!isUnauthorizedResponse(error)) {
					setAppError(error as Error);
				}
			},
			queryKey,
			enabled: false,
			retry: 0,
		},
		{
			errorIgnoreFilter: isResponseStatus(404),
		}
	);

	const fetch = useCallback(async () => {
		if (!isAuthorizationEnabled) {
			return;
		}

		await initQuery.refetch();
	}, [initQuery, isAuthorizationEnabled]);

	return { initServicesList: fetch };
};
